import Dialog from '@mui/material/Dialog'
import CircularProgress from '@mui/material/CircularProgress'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import mercadoPagoLogo from '../../../../../../assets/integrations/mercado-pago-auth.png'
import logoLuca from '../../../../../../assets/logoLuca.png'


const ACCOUNT_NUMBER_MAX_LENGTH = 20
const ACCOUNT_PREFIX = 'MP-'
export const ACCOUNT_NAME_MAX_LENGTH = ACCOUNT_NUMBER_MAX_LENGTH - ACCOUNT_PREFIX.length

const Field = ({ label, value }) => {
  if (!value) return null
  return (
    <div className='flex justify-between items-center py-2 border-b border-gray-100 last:border-0'>
      <span className='text-sm text-gray-500'>{label}</span>
      <span className='text-sm font-semibold text-gray-800'>{value}</span>
    </div>
  )
}

const MercadoPagoAccountConfirmModal = ({
  open,
  accountInfo,
  loading,
  error,
  accountName,
  onAccountNameChange,
  onConfirm,
  onCancel
}) => {
  const rutMismatch = !loading && !error && accountInfo?.rutMatch === false
  const trimmedName = (accountName || '').trim()
  const nameMissing = !loading && !error && !rutMismatch && trimmedName.length === 0

  return (
    <Dialog open={open} onClose={onCancel} maxWidth='sm' fullWidth PaperProps={{ className: 'rounded-2xl overflow-hidden' }}>
      <div className='bg-gradient-to-br from-accent2/10 to-white px-8 pt-8 pb-4 flex flex-col items-center text-center'>
        <div className='flex items-center gap-3 mb-5'>
          <img src={logoLuca} alt='Luca' className='h-8 w-auto' />
          <span className='text-gray-400 text-2xl font-light'>×</span>
          <img src={mercadoPagoLogo} alt='Mercado Pago' className='h-8 w-auto object-contain' />
        </div>
        {rutMismatch ? (
          <div className='w-full bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-left'>
            <div className='flex items-start gap-2'>
              <WarningAmberIcon className='text-red-600 shrink-0' sx={{ fontSize: 22, mt: '2px' }} />
              <div>
                <p className='text-red-700 text-sm font-semibold leading-snug'>
                  Este RUT difiere de la empresa que tienes actualmente.
                </p>
                <p className='text-red-600 text-xs mt-1 leading-relaxed'>
                  Cancela y cierra la sesión en Mercado Pago antes de volver a intentarlo con la cuenta correcta.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            <h2 className='text-xl font-bold text-gray-900 leading-snug'>
              ¡Estás a un paso de integrar<br />tu cuenta de Mercado Pago con Luca!
            </h2>
            <p className='text-sm text-gray-500 mt-2'>
              Confirma que esta es la cuenta que deseas conectar.
            </p>
          </>
        )}
      </div>

      <div className='px-8 pb-6'>
        {loading && (
          <div className='flex flex-col items-center justify-center py-8 gap-3'>
            <CircularProgress size={36} style={{ color: '#1CB2AF' }} />
            <p className='text-sm text-gray-500'>Obteniendo datos de la cuenta...</p>
          </div>
        )}

        {!loading && error && (
          <div className='py-4'>
            <div className='bg-red-50 border border-red-200 rounded-xl p-4 mb-4 text-center'>
              <p className='text-red-600 text-sm font-medium'>
                No se pudo obtener la información de la cuenta.
              </p>
              <p className='text-gray-500 text-xs mt-1'>
                Puedes continuar de todas formas o cancelar e intentar nuevamente.
              </p>
            </div>
          </div>
        )}

        {!loading && !error && accountInfo && (
          <div className='mt-2 mb-4 bg-gray-50 rounded-xl border border-gray-200 px-5 py-3'>
            <Field label='Nombre' value={accountInfo.firstName} />
            <Field label='Apellido' value={accountInfo.lastName} />
            <Field label='Email' value={accountInfo.email} />
            <Field
              label={accountInfo.identification?.type || 'RUT'}
              value={accountInfo.identification?.number}
            />
          </div>
        )}

        {!rutMismatch && !loading && (
          <div className='mb-5'>
            <label className='block text-sm font-semibold text-gray-700 mb-1'>
              Nombre de esta cuenta en Luca
            </label>
            <div className='flex items-stretch rounded-lg border border-gray-300 overflow-hidden focus-within:ring-2 focus-within:ring-accent2/40'>
              <span className='flex items-center px-3 bg-gray-100 text-gray-500 text-sm font-semibold border-r border-gray-300'>
                {ACCOUNT_PREFIX}
              </span>
              <input
                type='text'
                value={accountName || ''}
                onChange={e => onAccountNameChange(e.target.value.replace(/\s+/g, '').toUpperCase().slice(0, ACCOUNT_NAME_MAX_LENGTH))}
                maxLength={ACCOUNT_NAME_MAX_LENGTH}
                placeholder='NOMBRE-CUENTA'
                className='flex-1 px-3 py-2 text-sm outline-none'
              />
            </div>
            <div className='flex justify-between mt-1'>
              <span className='text-xs text-gray-400'>
                Sin espacios. Así distinguirás esta cuenta de las demás en Luca.
              </span>
              <span className='text-xs text-gray-400'>
                {trimmedName.length}/{ACCOUNT_NAME_MAX_LENGTH}
              </span>
            </div>
          </div>
        )}

        {!rutMismatch && !loading && !error && (
          <p className='text-xs text-gray-400 text-center mb-5'>
            ¿Esta no es la cuenta de la empresa que estás integrando? Cancela y cierra la sesión en Mercado Pago antes de volver a intentarlo.
          </p>
        )}

        <button
          onClick={onConfirm}
          disabled={loading || rutMismatch || nameMissing}
          className='w-full py-3 rounded-xl text-white font-bold text-sm bg-accent2 hover:bg-accent2/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
        >
          {rutMismatch
            ? 'Contacta a soporte para obtener más ayuda'
            : nameMissing
              ? 'Ingresa un nombre para la cuenta'
              : !loading && error
                ? 'Continuar de todas formas'
                : '¡Sí, integrar esta cuenta!'}
        </button>
        <button
          onClick={onCancel}
          className='w-full mt-2 py-2.5 rounded-xl text-gray-500 text-sm font-medium hover:bg-gray-100 transition-colors'
        >
          Cancelar
        </button>
      </div>
    </Dialog>
  )
}

export default MercadoPagoAccountConfirmModal
